import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    Grid,
    FormControlLabel,
    Switch,
    Alert,
    CircularProgress,
    Tooltip,
    Autocomplete,
    InputAdornment,
    Tabs,
    Tab,
    Stepper,
    Step,
    StepLabel,
    Popover,
    Fade,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add as AddIcon,
    Map as MapIcon,
    MyLocation as MyLocationIcon,
    Close as CloseIcon,
    HelpOutline as HelpOutlineIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    SkipNext as SkipNextIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { LatLngExpression } from 'leaflet';
import MapPickerDialog from '../MapPickerDialog';
import { parseGpsString } from '../MapView';
import JsdhNotifikaceService from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { LokaceSearchResult } from '../../api/JsdhNotifikaceService/generated/models/LokaceSearchResult';
import {
    useCreateSablonaMutation,
    useUpdateSablonaMutation,
    useTypyUdalostiQuery,
    useAllPodtypyQuery,
    useJednotkaQuery,
} from '../../api/JsdhNotifikaceService/service/AdminMutations';
import { useActiveTechnikaQuery } from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import type { Technika } from '../../api/JsdhNotifikaceService/generated/models/Technika';
import type {
    SablonaUdalosti,
    CreateSablonaRequest,
} from '../../api/JsdhNotifikaceService/generated';
import {
    COLOR_PALETTE,
    ICON_OPTIONS,
    getTemplateIcon,
    emptyForm,
} from '../../utils/templateHelpers';

// --- Leaflet helpers ---

const DEFAULT_CENTER: LatLngExpression = [49.8, 15.5];
const DEFAULT_ZOOM = 7;

const MapClickHandler: React.FC<{
    onLocationSelect: (lat: number, lng: number) => void;
}> = ({ onLocationSelect }) => {
    useMapEvents({
        click: (e) => {
            const { lat, lng } = e.latlng;
            onLocationSelect(
                Math.round(lat * 1000000) / 1000000,
                Math.round(lng * 1000000) / 1000000
            );
        },
    });
    return null;
};

const CenterUpdater: React.FC<{ position: LatLngExpression; zoom: number }> = ({ position, zoom }) => {
    const map = useMap();
    React.useEffect(() => {
        map.setView(position, zoom, { animate: true });
    }, [position, zoom, map]);
    return null;
};

const mapMarkerIcon = new L.Icon({
    iconUrl: '/maps/maker.png',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [1, -34],
});

// --- Types ---

interface SablonaWizardDialogProps {
    open: boolean;
    onClose: () => void;
    editingSablona?: SablonaUdalosti | null;
    nextPoradi: number;
}

type FormErrors = Partial<Record<keyof CreateSablonaRequest, string>>;

const STEPS = ['Šablona', 'Technika a místo', 'Nastavení'];

// --- Component ---

const SablonaWizardDialog: React.FC<SablonaWizardDialogProps> = ({
    open,
    onClose,
    editingSablona,
    nextPoradi,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Queries
    const { data: typy = [] } = useTypyUdalostiQuery();
    const { data: podtypy = [] } = useAllPodtypyQuery();
    const { data: jednotka } = useJednotkaQuery();
    const { data: aktivniTechnika = [] } = useActiveTechnikaQuery();

    // Mutations
    const createMutation = useCreateSablonaMutation();
    const updateMutation = useUpdateSablonaMutation();
    const isSaving = createMutation.isPending || updateMutation.isPending;

    // Form state
    const [form, setForm] = useState<CreateSablonaRequest>({ ...emptyForm });
    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState<FormErrors>({});

    // Step 1 popover state
    const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
    const [iconAnchor, setIconAnchor] = useState<HTMLElement | null>(null);

    // Step 2 state
    const [adresaTab, setAdresaTab] = useState(0);
    const [adresaInput, setAdresaInput] = useState('');
    const [lokaceOptions, setLokaceOptions] = useState<LokaceSearchResult[]>([]);
    const [isLoadingLokace, setIsLoadingLokace] = useState(false);
    const lokaceSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [technikaInput, setTechnikaInput] = useState('');
    const [selectedTechnikaIds, setSelectedTechnikaIds] = useState<number[]>([]);
    const [gpsPickerOpen, setGpsPickerOpen] = useState(false);

    // Computed
    const zbrojniceGps = useMemo(() => {
        if (jednotka?.zbrojniceLat && jednotka?.zbrojniceLng) {
            return `${jednotka.zbrojniceLat},${jednotka.zbrojniceLng}`;
        }
        return '';
    }, [jednotka]);

    // Reset on open
    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setErrors({});
            setAdresaInput('');
            setLokaceOptions([]);
            setAdresaTab(0);
            setTechnikaInput('');
            setColorAnchor(null);
            setIconAnchor(null);

            if (editingSablona) {
                setForm({
                    nazev: editingSablona.nazev,
                    ikona: editingSablona.ikona || '',
                    barva: editingSablona.barva || '#FF5722',
                    poradi: editingSablona.poradi,
                    typId: editingSablona.typId,
                    podtypId: editingSablona.podtypId,
                    udalost: editingSablona.udalost || '',
                    popis: editingSablona.popis || '',
                    obec: editingSablona.obec || '',
                    kraj: editingSablona.kraj || '',
                    castObce: editingSablona.castObce || '',
                    ulice: editingSablona.ulice || '',
                    cisloPopisne: editingSablona.cisloPopisne || '',
                    gps: editingSablona.gps || '',
                    objekt: editingSablona.objekt || '',
                    upresneni: editingSablona.upresneni || '',
                    zobrazitMapu: editingSablona.zobrazitMapu || false,
                    mapaGps: editingSablona.mapaGps || '',
                    mapaOkruh: editingSablona.mapaOkruh,
                    technikaIds: editingSablona.technikaIds || [],
                    technikaJednotky: editingSablona.technikaJednotky || [],
                    odeslatNotifikace: editingSablona.odeslatNotifikace ?? true,
                    generovatTts: editingSablona.generovatTts ?? true,
                    rychleVyhlaseni: editingSablona.rychleVyhlaseni ?? false,
                    predpoplach: editingSablona.predpoplach ?? false,
                    kritickaNotifikace: editingSablona.kritickaNotifikace ?? false,
                    userVyhlaseni: editingSablona.userVyhlaseni ?? false,
                });
                setSelectedTechnikaIds(editingSablona.technikaIds || []);
                setAdresaTab(editingSablona.zobrazitMapu ? 1 : 0);
            } else {
                setForm({ ...emptyForm, poradi: nextPoradi });
                setSelectedTechnikaIds([]);
            }
        }
    }, [open, editingSablona, nextPoradi]);

    // --- Validation ---

    const validateStep = (step: number): boolean => {
        const newErrors: FormErrors = {};
        if (step === 0) {
            if (!form.nazev?.trim()) newErrors.nazev = 'Název šablony je povinný';
            if (!form.predpoplach && !form.typId) newErrors.typId = 'Typ je povinný';
            if (!form.predpoplach && !form.podtypId) newErrors.podtypId = 'Podtyp je povinný';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // --- Navigation ---

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep((s) => s + 1);
        }
    };

    const handleBack = () => setActiveStep((s) => s - 1);
    const handleSkip = () => setActiveStep((s) => s + 1);

    const handleClose = () => {
        if (!isSaving) onClose();
    };

    const handleSubmit = () => {
        if (editingSablona) {
            updateMutation.mutate(
                { id: editingSablona.id, data: form },
                { onSuccess: () => onClose() }
            );
        } else {
            createMutation.mutate(form, {
                onSuccess: () => onClose(),
            });
        }
    };

    // --- Address handlers ---

    const handleAdresaInputChange = (value: string) => {
        setAdresaInput(value);
        if (lokaceSearchTimeoutRef.current) {
            clearTimeout(lokaceSearchTimeoutRef.current);
        }
        if (value.length >= 2) {
            lokaceSearchTimeoutRef.current = setTimeout(async () => {
                setIsLoadingLokace(true);
                try {
                    const results = await JsdhNotifikaceService.searchLokace(value);
                    setLokaceOptions(results);
                } catch {
                    setLokaceOptions([]);
                }
                setIsLoadingLokace(false);
            }, 300);
        } else {
            setLokaceOptions([]);
        }
    };

    const handleLokaceSelect = (value: string | LokaceSearchResult | null) => {
        if (!value) return;
        if (typeof value === 'string') {
            setAdresaInput(value);
        } else {
            setAdresaInput(value.displayName);
            setForm((prev) => ({
                ...prev,
                obec: value.obec || value.nazev,
                kraj: value.kraj || prev.kraj || '',
                castObce: value.castObce || prev.castObce || '',
                ulice: value.ulice || '',
                cisloPopisne: value.cisloPopisne || prev.cisloPopisne || '',
                gps: `${value.lat},${value.lng}`,
            }));
        }
    };

    // --- Technika handlers ---

    const addTechnikaFromAutocomplete = (value: string | Technika | null) => {
        if (!value) return;
        if (typeof value === 'string') {
            if (value.trim()) {
                setForm((prev) => ({
                    ...prev,
                    technikaJednotky: [...(prev.technikaJednotky || []), value.trim()],
                }));
            }
        } else {
            if (selectedTechnikaIds.includes(value.id)) return;
            const text = `${value.nazev} - ${value.volaciZnak}`;
            setForm((prev) => ({
                ...prev,
                technikaJednotky: [...(prev.technikaJednotky || []), text],
                technikaIds: [...(prev.technikaIds || []), value.id],
            }));
            setSelectedTechnikaIds((prev) => [...prev, value.id]);
        }
        setTechnikaInput('');
    };

    const addTechnika = () => {
        if (technikaInput.trim()) {
            setForm((prev) => ({
                ...prev,
                technikaJednotky: [...(prev.technikaJednotky || []), technikaInput.trim()],
            }));
            setTechnikaInput('');
        }
    };

    const removeTechnika = (index: number) => {
        const removedText = form.technikaJednotky?.[index];
        setForm((prev) => ({
            ...prev,
            technikaJednotky: prev.technikaJednotky?.filter((_, i) => i !== index),
        }));
        if (removedText) {
            const matchedTechnika = aktivniTechnika.find(
                (t) => `${t.nazev} - ${t.volaciZnak}` === removedText
            );
            if (matchedTechnika) {
                setSelectedTechnikaIds((prev) => prev.filter((id) => id !== matchedTechnika.id));
                setForm((prev) => ({
                    ...prev,
                    technikaIds: prev.technikaIds?.filter((id) => id !== matchedTechnika.id),
                }));
            }
        }
    };

    // ===================== STEP CONTENT =====================

    const renderStep0 = () => (
        <Box display="flex" flexDirection="column" gap={2}>
            {/* Name + Order */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 9 }}>
                    <TextField
                        label="Název šablony"
                        value={form.nazev}
                        onChange={(e) => {
                            setForm({ ...form, nazev: e.target.value });
                            if (errors.nazev) setErrors((prev) => ({ ...prev, nazev: undefined }));
                        }}
                        required
                        fullWidth
                        placeholder="např. Požár lesa - Krhanice"
                        error={!!errors.nazev}
                        helperText={errors.nazev}
                        autoFocus
                    />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                        label="Pořadí"
                        type="number"
                        value={form.poradi ?? 0}
                        onChange={(e) =>
                            setForm({ ...form, poradi: parseInt(e.target.value) || 0 })
                        }
                        fullWidth
                    />
                </Grid>
            </Grid>

            {/* Color + Icon + Preview */}
            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        Barva
                    </Typography>
                    <Box
                        onClick={(e) => setColorAnchor(e.currentTarget as HTMLElement)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 1,
                            '&:hover': { borderColor: 'primary.main' },
                        }}
                    >
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                bgcolor: form.barva || '#FF5722',
                                border: '2px solid',
                                borderColor: 'divider',
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            Změnit
                        </Typography>
                    </Box>
                    <Popover
                        open={Boolean(colorAnchor)}
                        anchorEl={colorAnchor}
                        onClose={() => setColorAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, maxWidth: 280 }}>
                            {COLOR_PALETTE.map((color) => (
                                <Box
                                    key={color}
                                    onClick={() => {
                                        setForm({ ...form, barva: color });
                                        setColorAnchor(null);
                                    }}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        bgcolor: color,
                                        cursor: 'pointer',
                                        border: form.barva === color ? '3px solid' : '2px solid transparent',
                                        borderColor: form.barva === color ? 'text.primary' : 'transparent',
                                        transition: 'transform 0.1s',
                                        '&:hover': { transform: 'scale(1.2)' },
                                    }}
                                />
                            ))}
                        </Box>
                    </Popover>
                </Grid>

                <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        Ikona
                    </Typography>
                    <Box
                        onClick={(e) => setIconAnchor(e.currentTarget as HTMLElement)}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.5,
                            '&:hover': { borderColor: 'primary.main' },
                        }}
                    >
                        {getTemplateIcon(form.ikona, 28)}
                        <Typography variant="body2" color="text.secondary">
                            Změnit
                        </Typography>
                    </Box>
                    <Popover
                        open={Boolean(iconAnchor)}
                        anchorEl={iconAnchor}
                        onClose={() => setIconAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 340 }}>
                            {ICON_OPTIONS.map((opt) => (
                                <Tooltip key={opt.key} title={opt.label}>
                                    <IconButton
                                        onClick={() => {
                                            setForm({ ...form, ikona: opt.key });
                                            setIconAnchor(null);
                                        }}
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2,
                                            border: form.ikona === opt.key ? '2px solid' : '2px solid transparent',
                                            borderColor: form.ikona === opt.key ? 'primary.main' : 'transparent',
                                            bgcolor: form.ikona === opt.key ? 'action.selected' : 'transparent',
                                        }}
                                    >
                                        {getTemplateIcon(opt.key, 24)}
                                    </IconButton>
                                </Tooltip>
                            ))}
                        </Box>
                    </Popover>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        Náhled
                    </Typography>
                    <Paper
                        sx={{
                            bgcolor: form.barva || '#78909c',
                            color: '#fff',
                            p: 1.5,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Box sx={{ opacity: 0.9 }}>
                            {getTemplateIcon(form.ikona, 28)}
                        </Box>
                        <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                            {form.nazev || 'Náhled šablony'}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Typ + Podtyp */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        select
                        label="Typ"
                        value={form.typId || ''}
                        onChange={(e) => {
                            setForm({
                                ...form,
                                typId: e.target.value ? Number(e.target.value) : undefined,
                                podtypId: undefined,
                            });
                            if (errors.typId) setErrors((prev) => ({ ...prev, typId: undefined }));
                        }}
                        required={!form.predpoplach}
                        fullWidth
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.typId}
                        helperText={errors.typId}
                    >
                        <option value="">-- Bez typu --</option>
                        {typy.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.nazev}
                            </option>
                        ))}
                    </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        select
                        label="Podtyp"
                        value={form.podtypId || ''}
                        onChange={(e) => {
                            setForm({
                                ...form,
                                podtypId: e.target.value ? Number(e.target.value) : undefined,
                            });
                            if (errors.podtypId) setErrors((prev) => ({ ...prev, podtypId: undefined }));
                        }}
                        required={!form.predpoplach}
                        fullWidth
                        disabled={!form.typId}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.podtypId}
                        helperText={errors.podtypId}
                    >
                        <option value="">-- Bez podtypu --</option>
                        {podtypy
                            .filter((p) => p.typId === form.typId)
                            .map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.nazev}
                                </option>
                            ))}
                    </TextField>
                </Grid>
            </Grid>

            {/* Popis */}
            <TextField
                label="Popis události"
                value={form.popis || ''}
                onChange={(e) => setForm({ ...form, popis: e.target.value })}
                fullWidth
                multiline
                rows={2}
                placeholder="např. Požár lesního porostu"
            />
        </Box>
    );

    const renderStep1 = () => (
        <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info" variant="outlined">
                Tento krok je volitelný — můžete ho přeskočit.
            </Alert>

            {/* Technika */}
            <Typography variant="subtitle2">Technika jednotky</Typography>
            <Box display="flex" gap={1}>
                <Autocomplete
                    freeSolo
                    fullWidth
                    size="small"
                    options={aktivniTechnika.filter((t) => !selectedTechnikaIds.includes(t.id))}
                    getOptionLabel={(option) =>
                        typeof option === 'string'
                            ? option
                            : `${option.nazev} - ${option.volaciZnak}${option.spz ? ` (${option.spz})` : ''}`
                    }
                    inputValue={technikaInput}
                    onInputChange={(_, newValue) => setTechnikaInput(newValue)}
                    onChange={(_, value) => addTechnikaFromAutocomplete(value)}
                    value={null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Vyberte z číselníku nebo zadejte vlastní"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addTechnika();
                                }
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Box>
                                <Typography variant="body2">
                                    {option.nazev} - <strong>{option.volaciZnak}</strong>
                                </Typography>
                                {option.spz && (
                                    <Typography variant="caption" color="text.secondary">
                                        SPZ: {option.spz}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    )}
                    clearOnBlur={false}
                    selectOnFocus
                    handleHomeEndKeys
                />
                <IconButton onClick={addTechnika} color="primary">
                    <AddIcon />
                </IconButton>
            </Box>
            {(form.technikaJednotky?.length ?? 0) > 0 && (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {form.technikaJednotky?.map((t, i) => (
                        <Chip
                            key={i}
                            label={t}
                            onDelete={() => removeTechnika(i)}
                            deleteIcon={<CloseIcon />}
                            size="small"
                        />
                    ))}
                </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Adresa a mapa */}
            <Typography variant="subtitle2">Adresa a mapa</Typography>
            <Autocomplete
                freeSolo
                options={lokaceOptions}
                loading={isLoadingLokace}
                getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.nazev
                }
                filterOptions={(x) => x}
                inputValue={adresaInput}
                onInputChange={(_, value) => handleAdresaInputChange(value)}
                onChange={(_, value) => handleLokaceSelect(value)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Vyhledat adresu"
                        fullWidth
                        placeholder="Zadejte obec, ulici nebo adresu..."
                        helperText="Vyberte z nabídky pro automatické vyplnění polí"
                    />
                )}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                        <li key={option.id} {...rest}>
                            <Box>
                                <Typography>{option.nazev}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.displayName}
                                </Typography>
                            </Box>
                        </li>
                    );
                }}
            />

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={adresaTab} onChange={(_, v) => {
                    setAdresaTab(v);
                    if (v === 1) {
                        if (!form.mapaGps) {
                            const defaultGps = form.gps || zbrojniceGps;
                            setForm((prev) => ({ ...prev, zobrazitMapu: true, mapaGps: defaultGps }));
                        } else {
                            setForm((prev) => ({ ...prev, zobrazitMapu: true }));
                        }
                    } else {
                        setForm((prev) => ({ ...prev, zobrazitMapu: false }));
                    }
                }}>
                    <Tab label="Detaily adresy" />
                    <Tab label="Mapa při vyhlášení" />
                </Tabs>
            </Box>

            {adresaTab === 0 && (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Obec"
                            value={form.obec || ''}
                            onChange={(e) => setForm({ ...form, obec: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Kraj"
                            value={form.kraj || ''}
                            onChange={(e) => setForm({ ...form, kraj: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            label="Část obce"
                            value={form.castObce || ''}
                            onChange={(e) => setForm({ ...form, castObce: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            label="Ulice"
                            value={form.ulice || ''}
                            onChange={(e) => setForm({ ...form, ulice: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            label="Číslo popisné"
                            value={form.cisloPopisne || ''}
                            onChange={(e) => setForm({ ...form, cisloPopisne: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Box display="flex" gap={1}>
                            <TextField
                                label="GPS"
                                value={form.gps || ''}
                                onChange={(e) => setForm({ ...form, gps: e.target.value })}
                                fullWidth
                                placeholder="50.0755,14.4378"
                            />
                            <Button
                                variant="outlined"
                                onClick={() => setGpsPickerOpen(true)}
                                sx={{ minWidth: 48, px: 1 }}
                            >
                                <MapIcon />
                            </Button>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Objekt"
                            value={form.objekt || ''}
                            onChange={(e) => setForm({ ...form, objekt: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Upřesnění"
                            value={form.upresneni || ''}
                            onChange={(e) => setForm({ ...form, upresneni: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            )}

            {adresaTab === 1 && (
                <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <Box display="flex" gap={1}>
                                <TextField
                                    label="GPS pro centrování mapy"
                                    value={form.mapaGps || ''}
                                    onChange={(e) => setForm({ ...form, mapaGps: e.target.value })}
                                    fullWidth
                                    placeholder="50.0755,14.4378"
                                    helperText="Klikněte na mapu nebo zadejte ručně"
                                />
                                {form.gps && form.gps !== form.mapaGps && (
                                    <Tooltip title="Použít GPS z adresy">
                                        <Button
                                            variant="outlined"
                                            onClick={() => setForm({ ...form, mapaGps: form.gps || '' })}
                                            sx={{ minWidth: 48, px: 1, mt: 0, mb: 'auto' }}
                                        >
                                            <MyLocationIcon />
                                        </Button>
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label="Okruh"
                                type="number"
                                value={form.mapaOkruh ?? ''}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        mapaOkruh: e.target.value ? parseInt(e.target.value) || undefined : undefined,
                                    })
                                }
                                fullWidth
                                placeholder="např. 5"
                                helperText={form.mapaOkruh ? `${form.mapaOkruh * 100} m` : 've stovkách metrů'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">× 100 m</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ height: isMobile ? 200 : 250, width: '100%', borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
                        <MapContainer
                            center={
                                (form.mapaGps && parseGpsString(form.mapaGps)) ||
                                (form.gps && parseGpsString(form.gps)) ||
                                (zbrojniceGps && parseGpsString(zbrojniceGps)) ||
                                DEFAULT_CENTER
                            }
                            zoom={
                                (form.mapaGps && parseGpsString(form.mapaGps)) ||
                                (form.gps && parseGpsString(form.gps)) ||
                                (zbrojniceGps && parseGpsString(zbrojniceGps))
                                    ? 14 : DEFAULT_ZOOM
                            }
                            style={{ height: '100%', width: '100%' }}
                            attributionControl={false}
                            key={open ? 'mapa-open' : 'mapa-closed'}
                        >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <MapClickHandler
                                onLocationSelect={(lat, lng) =>
                                    setForm((prev) => ({ ...prev, mapaGps: `${lat},${lng}` }))
                                }
                            />
                            {form.mapaGps && parseGpsString(form.mapaGps) && (
                                <>
                                    <Marker
                                        position={parseGpsString(form.mapaGps) as [number, number]}
                                        icon={mapMarkerIcon}
                                    />
                                    <CenterUpdater
                                        position={parseGpsString(form.mapaGps) as [number, number]}
                                        zoom={form.mapaOkruh && form.mapaOkruh > 10 ? 12 : 14}
                                    />
                                    {form.mapaOkruh && form.mapaOkruh > 0 && (
                                        <Circle
                                            center={parseGpsString(form.mapaGps) as [number, number]}
                                            radius={form.mapaOkruh * 100}
                                            pathOptions={{
                                                color: '#1976d2',
                                                fillColor: '#1976d2',
                                                fillOpacity: 0.1,
                                                weight: 2,
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </MapContainer>
                    </Box>
                </Box>
            )}
        </Box>
    );

    const renderStep2 = () => {
        const typName = form.typId ? typy.find((t) => t.id === form.typId)?.nazev : null;
        const podtypName = form.podtypId ? podtypy.find((p) => p.id === form.podtypId)?.nazev : null;

        return (
            <Grid container spacing={3}>
                {/* Settings */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Nastavení notifikací</Typography>
                    <Box display="flex" flexDirection="column" gap={1.5}>
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.odeslatNotifikace ?? true}
                                        onChange={(e) => setForm({ ...form, odeslatNotifikace: e.target.checked })}
                                    />
                                }
                                label="Odeslat notifikace"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 6, display: 'block', mt: -0.5 }}>
                                Odešle push notifikaci všem členům jednotky
                            </Typography>
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.generovatTts ?? true}
                                        onChange={(e) => setForm({ ...form, generovatTts: e.target.checked })}
                                    />
                                }
                                label="Generovat TTS"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 6, display: 'block', mt: -0.5 }}>
                                Automaticky vygeneruje hlasovou zprávu z popisu události
                            </Typography>
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.rychleVyhlaseni ?? false}
                                        onChange={(e) => setForm({ ...form, rychleVyhlaseni: e.target.checked })}
                                    />
                                }
                                label="Rychlé vyhlášení"
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 6, display: 'block', mt: -0.5 }}>
                                Přeskočí potvrzovací dialog při vyhlášení
                            </Typography>
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.predpoplach ?? false}
                                        onChange={(e) => setForm({ ...form, predpoplach: e.target.checked })}
                                        color="warning"
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Předpoplach
                                        <Tooltip title="Předpoplach slouží ke svolání hasičů ještě před příchodem oficiálního poplachu z KOPIS. Členům přijde notifikace s možností odpovědět JEDU/NEJEDU. Po příchodu poplachu se odpovědi automaticky přenesou.">
                                            <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                }
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 6, display: 'block', mt: -0.5 }}>
                                Svolání hasičů před oficiálním poplachem z KOPIS
                            </Typography>
                        </Box>

                        {form.predpoplach && (
                            <Box sx={{ pl: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.kritickaNotifikace ?? false}
                                            onChange={(e) => setForm({ ...form, kritickaNotifikace: e.target.checked })}
                                            color="error"
                                        />
                                    }
                                    label="Kritická notifikace"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ pl: 6, display: 'block', mt: -0.5 }}>
                                    Notifikace projde i přes tichý režim / nerušit
                                </Typography>
                            </Box>
                        )}

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.userVyhlaseni ?? false}
                                        onChange={(e) => setForm({ ...form, userVyhlaseni: e.target.checked })}
                                        color="info"
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Povolit vyhlášení členům
                                        <Tooltip title="Členové jednotky (ROLE_USER) budou moci z této šablony vyhlásit poplach v mobilní aplikaci. Nastavení TTS, notifikací a kritické notifikace zůstane zamčené dle šablony.">
                                            <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                                        </Tooltip>
                                    </Box>
                                }
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 6, display: 'block', mt: -0.5 }}>
                                Členové (ROLE_USER) budou moci vyhlásit z této šablony
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Summary */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Náhled šablony</Typography>
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Preview card */}
                        <Paper
                            sx={{
                                bgcolor: form.barva || '#78909c',
                                color: '#fff',
                                p: 1.5,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ opacity: 0.9 }}>
                                {getTemplateIcon(form.ikona, 28)}
                            </Box>
                            <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                                {form.nazev || 'Bez názvu'}
                            </Typography>
                        </Paper>

                        {/* Details */}
                        {(typName || podtypName) && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Typ události</Typography>
                                <Typography variant="body2">
                                    {[typName, podtypName].filter(Boolean).join(' / ')}
                                </Typography>
                            </Box>
                        )}

                        {form.popis && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Popis</Typography>
                                <Typography variant="body2">{form.popis}</Typography>
                            </Box>
                        )}

                        {(form.technikaJednotky?.length ?? 0) > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Technika</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                    {form.technikaJednotky?.map((t, i) => (
                                        <Chip key={i} label={t} size="small" />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {form.obec && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Lokace</Typography>
                                <Typography variant="body2">{form.obec}</Typography>
                            </Box>
                        )}

                        {form.zobrazitMapu && form.mapaGps && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Mapa</Typography>
                                <Typography variant="body2">
                                    GPS: {form.mapaGps}
                                    {form.mapaOkruh ? `, okruh ${form.mapaOkruh * 100} m` : ''}
                                </Typography>
                            </Box>
                        )}

                        {/* No data indicator */}
                        {!typName && !form.popis && !(form.technikaJednotky?.length) && !form.obec && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Pouze základní údaje
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0: return renderStep0();
            case 1: return renderStep1();
            case 2: return renderStep2();
            default: return null;
        }
    };

    // ===================== RENDER =====================

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            {editingSablona ? 'Upravit šablonu' : 'Nová šablona'}
                        </Typography>
                        <IconButton onClick={handleClose} size="small" disabled={isSaving}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Box sx={{ px: 3, pb: 2 }}>
                    <Stepper activeStep={activeStep} alternativeLabel={!isMobile}>
                        {STEPS.map((label, i) => (
                            <Step key={label}>
                                <StepLabel
                                    optional={i === 1 ? (
                                        <Typography variant="caption">Volitelné</Typography>
                                    ) : undefined}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <DialogContent sx={{ minHeight: isMobile ? undefined : 400 }}>
                    <Fade in key={activeStep} timeout={200}>
                        <Box>{renderStepContent(activeStep)}</Box>
                    </Fade>
                </DialogContent>

                <DialogActions sx={{
                    position: 'sticky',
                    bottom: 0,
                    bgcolor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider',
                    py: 1.5,
                    px: 3,
                    justifyContent: 'space-between',
                }}>
                    {/* Left side */}
                    <Box>
                        {activeStep === 0 ? (
                            <Button onClick={handleClose} disabled={isSaving}>
                                Zrušit
                            </Button>
                        ) : (
                            <Button
                                onClick={handleBack}
                                disabled={isSaving}
                                startIcon={<ArrowBackIcon />}
                            >
                                Zpět
                            </Button>
                        )}
                    </Box>

                    {/* Right side */}
                    <Box display="flex" gap={1}>
                        {activeStep === 1 && (
                            <Button
                                onClick={handleSkip}
                                disabled={isSaving}
                                startIcon={<SkipNextIcon />}
                            >
                                Přeskočit
                            </Button>
                        )}

                        {activeStep < STEPS.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={isSaving}
                                endIcon={<ArrowForwardIcon />}
                            >
                                Další
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isSaving}
                                startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
                            >
                                {editingSablona ? 'Uložit' : 'Vytvořit'}
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            <MapPickerDialog
                open={gpsPickerOpen}
                onClose={() => setGpsPickerOpen(false)}
                onSelect={(lat, lng) => setForm({ ...form, gps: `${lat},${lng}` })}
                initialGps={form.gps}
            />
        </>
    );
};

export default SablonaWizardDialog;
